import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from 'store';
import {
  type Condition,
  CONDITIONS,
  createConditionName,
} from 'types/notification';
import {
  type RegistrationStep,
  REGISTRATION_STEP,
} from 'page/NotificationPage/types/registrationStep';
import { NotificationFormFields } from 'page/NotificationPage/types/notificationFormField';

/**
 * お知らせ登録画面の状態
 */
interface RegistrationState {
  /**
   * 登録モーダル表示フラグ
   */
  isOpen: boolean;
  /**
   * 登録のステップ
   */
  step: RegistrationStep;
  /**
   * タイトル
   */
  title: string;
  /**
   * 本文
   */
  message: string;
  /**
   * お知らせ種別（重要なら true）
   */
  isHigh: boolean;
  /**
   * 開始日
   */
  startDate: string;
  /**
   * 終了日
   */
  endDate: string;
  /**
   * 配信対象顧客
   */
  condition: Condition;
}

/**
 * お知らせ登録画面の初期状態
 */
const initialState: RegistrationState = {
  isOpen: false,
  step: REGISTRATION_STEP.first,
  title: '',
  message: '',
  isHigh: false,
  startDate: '',
  endDate: '',
  condition: CONDITIONS.ALL,
};

/**
 * お知らせ登録画面の slice(reducer + action)
 */
export const registrationSlice = createSlice({
  name: 'registration',
  initialState,
  reducers: {
    init: () => initialState,
    modalOpened: (state) => {
      state.isOpen = true;
    },
    stepMovedToSecond: (state) => {
      state.step = REGISTRATION_STEP.second;
    },
    stepMovedToFirst: (state) => {
      state.step = REGISTRATION_STEP.first;
    },
    conditionUpdated: (
      state,
      action: PayloadAction<{ condition: Condition }>,
    ) => {
      state.condition = action.payload.condition;
    },
    formGroupUpdated: (
      state,
      action: PayloadAction<{
        form: NotificationFormFields;
      }>,
    ) => {
      state.title = action.payload.form.title;
      state.message = action.payload.form.message;
      state.isHigh = action.payload.form.isHigh;
      state.startDate = action.payload.form.startDate;
      state.endDate = action.payload.form.endDate;
      state.condition = action.payload.form.condition;
    },
  },
});

/**
 * お知らせ登録画面の action
 */
export const registrationActions = registrationSlice.actions;

/**
 * お知らせ登録画面の selectors
 */
export const registrationSelectors = {
  isOpenFirstModal: (state: RootState) =>
    state.notification.registration.isOpen &&
    state.notification.registration.step === REGISTRATION_STEP.first,
  isOpenSecondModal: (state: RootState) =>
    state.notification.registration.isOpen &&
    state.notification.registration.step === REGISTRATION_STEP.second,
  title: (state: RootState) => state.notification.registration.title,
  message: (state: RootState) => state.notification.registration.message,
  isHigh: (state: RootState) => state.notification.registration.isHigh,
  startDate: (state: RootState) => state.notification.registration.startDate,
  endDate: (state: RootState) => state.notification.registration.endDate,
  condition: (state: RootState) => state.notification.registration.condition,
  // 表示用の配信対象顧客
  conditionForDisplay: (state: RootState) => {
    const { condition } = state.notification.registration;

    return createConditionName(condition);
  },
  // 顧客一覧表示フラグ
  shouldShowCustomerList: (state: RootState) =>
    state.notification.registration.condition !== CONDITIONS.ALL,
} as const;
